import { useState, useEffect, useMemo, useRef } from 'react'
import { Search, X, Package } from 'lucide-react'
import { EmptyState } from '@components/ui/EmptyState'
import { useQuoteStore } from '../store'
import { EXCHANGE_RATES } from '../data'
import type { PlanType, Product } from '../types'
import { getAuthHeaders } from '@api/client'
import { ProductFilterPopover } from './ProductFilterPopover'
import { matchesProductFilter, activeFilterCount, EMPTY_PRODUCT_FILTER, type ProductFilterState } from '../utils/productFilter'

// Target gross margin per plan tier. Anything ≥ target → green;
// 0–10pp below → amber; > 10pp below → red.
// Empty plan = treat as Base.
const TARGET_MARGIN_BY_PLAN: Record<PlanType | '', number> = {
  '': 80,
  Basic: 80,
  Professional: 70,
  Enterprise: 60,
}

function marginColorClass(marginPct: number, target: number): string {
  if (marginPct >= target) return 'text-success'
  if (marginPct >= target - 10) return 'text-warning'
  return 'text-danger'
}

// Map snake_case API product to camelCase Product interface
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapApiProduct = (p: any): Product => ({
  id: p.id,
  name: p.name,
  currency: p.currency,
  basePrice: p.basePrice ?? p.base_price ?? 0,
  proPrice: p.proPrice ?? p.pro_price ?? 0,
  enterprisePrice: p.enterprisePrice ?? p.enterprise_price ?? 0,
  costPrice: p.costPrice ?? p.cost_price ?? 0,
  vendor: p.vendor ?? p.category ?? undefined,
  category: p.category ?? undefined,
  oldName: p.oldName ?? p.old_name ?? undefined,
})

// Helper to format currency
const formatCurrency = (value: number, currency: string): string => {
  const code = currency || 'AUD'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: code,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

// Helper to convert currency
const convertCurrency = (amount: number, from: string, to: string): number => {
  if (from === to) return amount
  return amount * (EXCHANGE_RATES[from]?.[to] ?? 1)
}

export function ProductsPanel() {
  const {
    products,
    setProducts,
    productSearch,
    setProductSearch,
    productsLoaded,
    setProductsLoaded,
    addProducts,
    selectedProducts,
    removeProduct,
    clearAllProducts,
    updateProductVolume,
    updateProductDiscount,
    updateProductUplift,
    updateProductCostPrice,
    details,
    quoteLocks,
  } = useQuoteStore()

  const [loading, setLoading] = useState(false)
  const [localSelectedIds, setLocalSelectedIds] = useState<number[]>([])
  const [productFilter, setProductFilter] = useState<ProductFilterState>(EMPTY_PRODUCT_FILTER)
  const selectRef = useRef<HTMLSelectElement>(null)
  const { displayCurrency } = details

  // Load products on mount
  useEffect(() => {
    if (!productsLoaded) {
      loadProducts()
    }
  }, [productsLoaded])

  const loadProducts = async () => {
    setLoading(true)
    try {
      // Authenticated call — without the Bearer token the worker returns 401
      // and we silently fall through to the (potentially stale) static file.
      const apiResponse = await fetch('/api/products', { headers: getAuthHeaders() })
      if (apiResponse.ok) {
        const data = await apiResponse.json()
        // API returns array directly, or wrapped in { products: [...] }
        const rawList = Array.isArray(data) ? data : data.products
        if (rawList && rawList.length > 0) {
          // Map snake_case API fields to camelCase Product interface
          const productsList = rawList.map(mapApiProduct)
          setProducts(productsList)
          setProductsLoaded(true)
          setLoading(false)
          return
        }
      } else {
        console.warn(`Products API returned ${apiResponse.status}; falling back to static file (may be stale).`)
      }
    } catch {
      console.log('API products not available, trying static file...')
    }

    try {
      const response = await fetch('/products.json')
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      setProducts(data.products)
      setProductsLoaded(true)
    } catch (error) {
      console.error('Failed to load products:', error)
    }
    setLoading(false)
  }

  // Filter products by search text AND the attribute filter (country/type/cascade/data source).
  const filteredProducts = useMemo(() => {
    const search = productSearch.trim().toLowerCase()
    const filterOn = activeFilterCount(productFilter) > 0
    if (!search && !filterOn) return products
    return products.filter((p) => {
      if (filterOn && !matchesProductFilter(p, productFilter)) return false
      if (!search) return true
      return (
        p.name.toLowerCase().includes(search) ||
        p.vendor?.toLowerCase().includes(search) ||
        p.currency.toLowerCase().includes(search)
      )
    })
  }, [products, productSearch, productFilter])

  // Get already selected product IDs
  const alreadySelectedIds = useMemo(
    () => new Set(selectedProducts.map((p) => p.id)),
    [selectedProducts]
  )

  // Available products (not already selected)
  const availableProducts = useMemo(
    () => filteredProducts.filter((p) => !alreadySelectedIds.has(p.id)),
    [filteredProducts, alreadySelectedIds]
  )

  const handleAddSelected = () => {
    if (localSelectedIds.length === 0) return
    const productsToAdd = products.filter((p) => localSelectedIds.includes(p.id))
    addProducts(productsToAdd)
    setLocalSelectedIds([])
    if (selectRef.current) {
      selectRef.current.selectedIndex = -1
    }
  }

  const handleSelectAll = () => {
    addProducts(availableProducts)
    setLocalSelectedIds([])
  }

  const handleClearAll = () => {
    if (selectedProducts.length === 0) return
    if (confirm(`Remove all ${selectedProducts.length} products?`)) {
      clearAllProducts()
    }
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selected = Array.from(e.target.selectedOptions).map((opt) => Number(opt.value))
    setLocalSelectedIds(selected)
  }

  return (
    <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4">
      <h3 className="text-sm font-medium text-[var(--text-primary)] mb-3 flex items-center gap-2">
        <span>📦</span> Products & Services
      </h3>

      {/* Search + add product UI — hidden while quote is locked.
          Locking the whole add-products block prevents the submitter
          accidentally adding new SKUs after submitting for review. */}
      {!quoteLocks.all && (
        <>
          {/* Search */}
          <div className="relative mb-3">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              type="text"
              placeholder="Search products..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Multi-select dropdown */}
          <select
            ref={selectRef}
            multiple
            size={8}
            onChange={handleSelectChange}
            className="w-full mb-3 text-[11px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary"
          >
            {loading ? (
              <option disabled>Loading products...</option>
            ) : availableProducts.length === 0 ? (
              <option disabled>No products available</option>
            ) : (
              availableProducts.map((product) => (
                <option
                  key={product.id}
                  value={product.id}
                  className="py-1 px-2 hover:bg-primary/20"
                >
                  {product.name} ({product.currency})
                </option>
              ))
            )}
          </select>

          {/* Product count and actions */}
          <div className="flex items-center justify-between mb-3 text-[10px]">
            <span className="text-success">
              Showing {availableProducts.length} products
            </span>
            <div className="flex gap-2">
              <button
                onClick={handleAddSelected}
                disabled={localSelectedIds.length === 0}
                className="px-3 py-1 bg-primary text-white rounded text-[11px] font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Selected
              </button>
              <button
                onClick={handleSelectAll}
                disabled={availableProducts.length === 0}
                className="px-3 py-1 bg-[var(--bg-tertiary)] text-[var(--text-primary)] border border-[var(--border-color)] rounded text-[11px] hover:bg-[var(--border-color)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Select All
              </button>
              <ProductFilterPopover products={products} value={productFilter} onChange={setProductFilter} />
              <button
                onClick={handleClearAll}
                disabled={selectedProducts.length === 0}
                className="px-3 py-1 bg-[var(--bg-tertiary)] text-[var(--text-primary)] border border-[var(--border-color)] rounded text-[11px] hover:bg-[var(--border-color)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear All
              </button>
            </div>
          </div>
        </>
      )}

      {/* Selected Products */}
      <div className="border-t border-[var(--border-color)] pt-3">
        <h4 className="text-[11px] font-medium text-[var(--text-secondary)] mb-3">
          Selected Products
        </h4>

        {selectedProducts.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No products added"
            description="Add products to start building your quote"
          />
        ) : (
          <div className="space-y-3 max-h-[500px] overflow-y-auto">
            {selectedProducts.map((product) => {
              const effectivePrice = product.effectivePrice ?? 0
              const planPrice = product.planPrice ?? 0
              const hasDiscount = product.discountType !== 'none'
              const hasUplift = product.upliftType !== 'none'
              const hasAdjustment = hasDiscount || hasUplift
              // Show original-strikethrough + arrow whenever either adjustment moves the price.
              const showOriginalPrice = hasAdjustment && effectivePrice !== planPrice
              const convertedPrice = convertCurrency(
                effectivePrice,
                product.currency,
                displayCurrency
              )
              const annualTotal = product.volume * convertedPrice
              // Per-SKU margin: (price − cost) / price * 100. Both in the
              // product's native currency — currency conversion isn't needed
              // because the ratio is currency-independent.
              const unitCost = product.customCostPrice ?? product.costPrice ?? 0
              const marginPct = effectivePrice > 0
                ? ((effectivePrice - unitCost) / effectivePrice) * 100
                : 0
              const targetMargin = TARGET_MARGIN_BY_PLAN[details.customerPlan as PlanType | ''] ?? 80
              const marginCls = marginColorClass(marginPct, targetMargin)

              return (
                <div
                  key={product.uniqueId}
                  className="p-3 bg-[var(--bg-tertiary)] rounded-lg border border-[var(--border-color)]"
                >
                  {/* Product Header with Name, Price and Remove Button */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-[12px] text-[var(--text-primary)] font-medium">
                        {product.name}
                      </div>
                      <div className="text-[11px]">
                        {showOriginalPrice ? (
                          <>
                            <span className="text-[var(--text-muted)] line-through">
                              {product.currency} {planPrice.toFixed(2)}
                            </span>
                            <span className="text-[var(--text-muted)] mx-1">→</span>
                            <span className={hasUplift ? 'text-warning' : 'text-success'}>
                              {product.currency} {effectivePrice.toFixed(2)}
                            </span>
                          </>
                        ) : (
                          <span className="text-[var(--text-muted)]">
                            {product.currency} {effectivePrice.toFixed(2)}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeProduct(product.uniqueId)}
                      disabled={quoteLocks.all}
                      className="p-1.5 bg-danger/80 hover:bg-danger text-[var(--text-primary)] rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-danger/80"
                      title={quoteLocks.all ? 'Locked while under review' : 'Remove product'}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  {/* Controls — two stacked rows so the Adjustment widget
                      doesn't compress the always-visible Cost/Margin/Total
                      readouts when an adjustment is active. */}
                  {/* Row 1: Annual Volume + Cost + Margin + Annual Total.
                      items-start pins all four header labels to one exact
                      baseline; the read-only Margin/Total values sit in a
                      fixed-height (h-[30px], ~input height) centred flex box so
                      they still line up with the input fields. Aligning by
                      label-top — rather than items-end — means a short
                      read-only cell can no longer drag its label downward. */}
                  <div className="flex items-start gap-3">
                    {/* Annual Volume */}
                    <div className="w-24">
                      <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                        Annual Volume
                      </label>
                      <input
                        type="number"
                        value={product.volume || ''}
                        onChange={(e) =>
                          updateProductVolume(product.uniqueId, Number(e.target.value) || 0)
                        }
                        onFocus={(e) => {
                          if (product.volume === 0) e.target.select()
                        }}
                        disabled={quoteLocks.all}
                        min={0}
                        placeholder="0"
                        className="w-full px-2 py-1.5 text-[11px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Cost Field - Grey/secondary styling */}
                    <div className="w-24">
                      <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                        Cost ({product.currency})
                      </label>
                      <input
                        type="number"
                        value={
                          product.customCostPrice !== null
                            ? product.customCostPrice
                            : product.costPrice
                        }
                        onChange={(e) =>
                          updateProductCostPrice(product.uniqueId, Number(e.target.value) || 0)
                        }
                        disabled={quoteLocks.all}
                        min={0}
                        step={0.01}
                        className="w-full px-2 py-1.5 text-[11px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>

                    {/* Margin % — colour-coded against the selected plan's target */}
                    <div className="text-right min-w-[60px]">
                      <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                        Margin
                      </label>
                      <div className="flex h-[30px] items-center justify-end">
                        <span
                          className={`text-[11px] font-mono ${marginCls}`}
                          title={`Target for ${details.customerPlan || 'Basic'}: ${targetMargin}%`}
                        >
                          {effectivePrice > 0 ? `${marginPct.toFixed(1)}%` : '—'}
                        </span>
                      </div>
                    </div>

                    {/* Annual Total — empty spacer label keeps the value level
                        with the inputs/Margin under items-start. */}
                    <div className="text-right min-w-[70px] ml-auto">
                      <label className="block text-[10px] mb-1 select-none" aria-hidden="true">
                        &nbsp;
                      </label>
                      <div className="flex h-[30px] items-center justify-end">
                        <span className="text-[12px] font-medium text-primary">
                          {formatCurrency(annualTotal, displayCurrency)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Adjustment + (Type + Amount when active). On its own
                      row directly under Annual Volume so AEs can dial in a
                      discount/uplift without it visually clobbering the cost
                      and margin readouts above. */}
                  <div className="flex items-end gap-3 mt-3">
                    {/* Adjustment — Discount or Uplift. Mutually exclusive:
                        ticking one clears the other via the store action so
                        the AE never sees both active at once. Header label
                        intentionally omitted — the Discount/Uplift checkboxes
                        are self-describing. */}
                    <div>
                      <div className={`flex items-center gap-3 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded px-2 py-1.5 ${quoteLocks.all ? 'opacity-50' : ''}`}>
                        <label className={`flex items-center gap-1 ${quoteLocks.all ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={hasDiscount}
                            disabled={quoteLocks.all}
                            onChange={(e) => {
                              if (e.target.checked) {
                                updateProductDiscount(product.uniqueId, 'absolute', product.discountValue || 0)
                              } else {
                                updateProductDiscount(product.uniqueId, 'none', 0)
                              }
                            }}
                            className="w-3 h-3 accent-primary disabled:cursor-not-allowed"
                          />
                          <span className="text-[11px] text-[var(--text-primary)]">Discount</span>
                        </label>
                        <label className={`flex items-center gap-1 ${quoteLocks.all ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                          <input
                            type="checkbox"
                            checked={hasUplift}
                            disabled={quoteLocks.all}
                            onChange={(e) => {
                              if (e.target.checked) {
                                updateProductUplift(product.uniqueId, 'absolute', product.upliftValue || 0)
                              } else {
                                updateProductUplift(product.uniqueId, 'none', 0)
                              }
                            }}
                            className="w-3 h-3 accent-primary disabled:cursor-not-allowed"
                          />
                          <span className="text-[11px] text-warning">Uplift</span>
                        </label>
                      </div>
                    </div>

                    {/* Type + Amount — only render when one adjustment is active */}
                    {hasAdjustment && (
                      <>
                        <div>
                          <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                            Type
                          </label>
                          <div className={`flex items-center gap-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded px-2 py-1.5 ${quoteLocks.all ? 'opacity-50' : ''}`}>
                            {(['absolute', 'percent'] as const).map((t) => {
                              const isActive = hasUplift ? product.upliftType === t : product.discountType === t
                              return (
                                <label key={t} className={`flex items-center gap-1 ${quoteLocks.all ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                  <input
                                    type="radio"
                                    name={`atype-${product.uniqueId}`}
                                    checked={isActive}
                                    disabled={quoteLocks.all}
                                    onChange={() => {
                                      if (hasUplift) {
                                        updateProductUplift(product.uniqueId, t, product.upliftValue || 0)
                                      } else {
                                        updateProductDiscount(product.uniqueId, t, product.discountValue || 0)
                                      }
                                    }}
                                    className="w-3 h-3 accent-primary disabled:cursor-not-allowed"
                                  />
                                  <span className="text-[11px] text-[var(--text-primary)]">
                                    {t === 'absolute' ? '$' : '%'}
                                  </span>
                                </label>
                              )
                            })}
                          </div>
                        </div>

                        <div className="w-20">
                          <label className="block text-[10px] text-[var(--text-muted)] mb-1">
                            Amount
                          </label>
                          <input
                            type="number"
                            value={(hasUplift ? product.upliftValue : product.discountValue) || ''}
                            onChange={(e) => {
                              const v = Number(e.target.value) || 0
                              if (hasUplift) {
                                updateProductUplift(product.uniqueId, product.upliftType, v)
                              } else {
                                updateProductDiscount(product.uniqueId, product.discountType, v)
                              }
                            }}
                            onFocus={(e) => {
                              const val = hasUplift ? product.upliftValue : product.discountValue
                              if (val === 0) e.target.select()
                            }}
                            disabled={quoteLocks.all}
                            min={0}
                            // step="any" disables browser step validation. A
                            // numeric step like 0.01 trips floating-point math
                            // in some browsers (0.07 / 0.01 = 7.000…001) so
                            // values like 0.02 or 0.07 get incorrectly rejected.
                            step="any"
                            placeholder="0"
                            className="w-full px-2 py-1.5 text-[11px] bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {selectedProducts.length > 0 && (
          <p className="text-[10px] text-[var(--text-muted)] mt-3 text-right">
            * Total prices indicate yearly totals
          </p>
        )}
      </div>
    </div>
  )
}
